import React from 'react'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { RadarOutlined } from '@mui/icons-material'
import { Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { SettingsBox } from 'src/Elements/Data'
import { B1Battery } from '@e3dc-react/icons'
import useModals from 'src/Hooks/useModals'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'

interface Props {
  installationId?: number
  emcDeviceLinkId?: number
  emcSerialnumber: string | undefined
  isEmcOnline: boolean
  plannedStorageCount: number
  isPlannedStorageCountLoading?: boolean
  qntmSerialNumbers?: string[]
}

const StorageSettingsCard: React.FC<Props> = ({
  installationId,
  emcDeviceLinkId,
  emcSerialnumber,
  isEmcOnline,
  plannedStorageCount,
  isPlannedStorageCountLoading,
  qntmSerialNumbers,
}) => {
  const { t } = useCustomTranslation()
  const { openScanNetworkForQntmsDialog } = useModals()

  const sortedSerialNumbers = Array.from(new Set(qntmSerialNumbers ?? [])).sort((a, b) => a.localeCompare(b))
  const hasLinkedDevices = sortedSerialNumbers.length > 0

  const handleScanClick = (): void => {
    if (!emcSerialnumber || !installationId || !emcDeviceLinkId) return
    openScanNetworkForQntmsDialog({ emcSerialnumber, installationId, emcDeviceLinkId })
  }

  return (
    <AccordionItem title={t('installation-configuration.storage.settings.title')} expanded accordionDetailsSx={{ px: 4, py: 2 }}>
      <Stack>
        <PlannedStorageSettingsBox
          emcSerialnumber={emcSerialnumber}
          installationId={installationId}
          emcDeviceLinkId={emcDeviceLinkId}
          isEmcOnline={isEmcOnline}
          plannedStorageCount={plannedStorageCount}
          isPlannedStorageCountLoading={isPlannedStorageCountLoading}
          onScanClick={handleScanClick}
        />

        <StorageSerialNumbersBox sortedSerialNumbers={sortedSerialNumbers} hasLinkedDevices={hasLinkedDevices} />
      </Stack>
    </AccordionItem>
  )
}

// #region PlannedStorageSettingsBox
interface PlannedStorageSettingsBoxProps {
  emcSerialnumber: string | undefined
  installationId: number | undefined
  emcDeviceLinkId: number | undefined
  isEmcOnline: boolean
  plannedStorageCount: number
  isPlannedStorageCountLoading?: boolean
  onScanClick: () => void
}

const PlannedStorageSettingsBox: React.FC<PlannedStorageSettingsBoxProps> = ({
  emcSerialnumber,
  installationId,
  emcDeviceLinkId,
  isEmcOnline,
  plannedStorageCount,
  isPlannedStorageCountLoading,
  onScanClick,
}) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <SettingsBox
      title={t('installation-configuration.storage.settings.planned-count')}
      suffix={
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          gap={4}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="flex-end"
          sx={{ width: '100%' }}
        >
          <Typography variant="body2" align={'center'}>
            {isPlannedStorageCountLoading ? '-' : plannedStorageCount}
          </Typography>
          <OptionalTooltipWrapper titleKey={'installation-configuration.storage.settings.scan-disabled-tooltip'} hasTooltip={!isEmcOnline}>
            <Button
              variant="outlined"
              onClick={onScanClick}
              disabled={!isEmcOnline || !emcSerialnumber || !installationId || !emcDeviceLinkId}
              startIcon={<RadarOutlined fontSize="small" />}
              fullWidth={isXs}
            >
              {t('installation-configuration.storage.settings.scan-button')}
            </Button>
          </OptionalTooltipWrapper>
        </Stack>
      }
    />
  )
}
// #endregion

// #region StorageSerialNumbersBox
interface StorageSerialNumbersBoxProps {
  sortedSerialNumbers: string[]
  hasLinkedDevices: boolean
}

const StorageSerialNumbersBox: React.FC<StorageSerialNumbersBoxProps> = ({ sortedSerialNumbers, hasLinkedDevices }) => {
  const { t } = useCustomTranslation()

  return (
    <SettingsBox title={t('common.serialnumber')}>
      {hasLinkedDevices ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} sx={{ mt: 2 }}>
          {sortedSerialNumbers.map(serialNumber => (
            <Stack
              key={serialNumber}
              direction="row"
              alignItems="center"
              gap={1}
              sx={theme => ({
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.grey.grey06}`,
                borderRadius: 1,
                display: 'flex',
                padding: '12px 24px',
                alignItems: 'center',
                gap: 1,
              })}
            >
              <B1Battery fontSize="medium" />
              <Typography variant="h3">{serialNumber}</Typography>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('installation-configuration.storage.scan.dialog.no-results')}
        </Typography>
      )}
    </SettingsBox>
  )
}
// #endregion

export default StorageSettingsCard
